import React, { useRef, useState } from "react";
import { CheckSquare, Layers, Square, Trash2 } from "lucide-react";
import { idNative } from "@/lib/idCompat";
import { localizedIdentification } from "@/lib/localizedId";
import { stateCode } from "@/lib/usStates";
import { useReturnableRoute } from "@/hooks/useReturnableRoute";
import { isUnidentified } from "@/lib/galleryUtils";

/**
 * Gallery grid card. Supports:
 *  - long-press to delete (touch + mouse) with a 220ms hint reveal at 550ms fire
 *  - master-detail on lg+ via `onSelectInline`
 *  - bulk-select mode with checkbox overlay
 *  - "needs ID" overlay for unidentified scans
 *  - multi-photo badge when `extra_images_count > 0`
 */
export const PlantCard = ({
  item,
  onRequestDelete,
  t,
  userLang,
  userState,
  selectMode,
  selected,
  onToggleSelect,
  onSelectInline,
  activeInline,
  onTagClick,
  activeTags,
}) => {
  const id = localizedIdentification(item, userLang) || {};
  const needsId = isUnidentified(item);
  const openDetail = useReturnableRoute();
  const longPressTimer = useRef(null);
  const holdRevealTimer = useRef(null);
  const triggered = useRef(false);
  const [isHolding, setIsHolding] = useState(false);

  const startLongPress = () => {
    triggered.current = false;
    // At 220 ms we visually reveal the delete affordance so the user sees
    // "held, delete coming"; at 550 ms we actually open the confirm dialog.
    holdRevealTimer.current = setTimeout(() => setIsHolding(true), 220);
    longPressTimer.current = setTimeout(() => {
      triggered.current = true;
      onRequestDelete(item.scan_id);
    }, 550);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    if (holdRevealTimer.current) { clearTimeout(holdRevealTimer.current); holdRevealTimer.current = null; }
    setIsHolding(false);
  };
  const onClickCard = (e) => {
    if (selectMode) { e.preventDefault(); onToggleSelect(item.scan_id); return; }
    if (triggered.current) { e.preventDefault(); triggered.current = false; return; }
    // On desktop (lg+) with master-detail, open the side pane instead of navigating away.
    if (onSelectInline) { e.preventDefault(); onSelectInline(item.scan_id); return; }
    // Preserve the current Gallery URL (path + query — filter, tags, page…)
    // so the back arrow on Result returns to the exact same filtered view.
    openDetail(`/plant/${item.scan_id}`, { scan: item });
  };

  return (
    <div className={`relative bg-white rounded-3xl border overflow-hidden group transition ${
        selected
          ? "border-leaf ring-2 ring-leaf/40"
          : activeInline
            ? "border-leaf ring-2 ring-leaf/60"
            : "border-[#E8E5DF]"
      }`}
      data-testid={`plant-card-${item.scan_id}`}>
      <button type="button" onClick={onClickCard}
        onTouchStart={selectMode ? undefined : startLongPress} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress} onTouchCancel={cancelLongPress}
        onMouseDown={selectMode ? undefined : startLongPress} onMouseUp={cancelLongPress} onMouseLeave={cancelLongPress}
        onContextMenu={(e) => { if (!selectMode) { e.preventDefault(); onRequestDelete(item.scan_id); } }}
        className="block w-full text-left"
        data-testid={`plant-card-button-${item.scan_id}`}>
        <div className="relative aspect-square bg-parchment">
          <img
            src={item.thumb_base64 || item.image_base64}
            alt={id.common_name || ""}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {needsId && (
            <div className="absolute inset-0 bg-leaf-ink/35 backdrop-blur-[1px] flex items-center justify-center" data-testid={`needs-id-overlay-${item.scan_id}`}>
              <span className="font-display text-white text-4xl drop-shadow">?</span>
            </div>
          )}
          {selectMode && (
            <div className={`absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center transition ${selected ? "bg-leaf text-white" : "bg-white/90 text-leaf-mist"}`} data-testid={`select-check-${item.scan_id}`}>
              {selected ? <CheckSquare className="w-4 h-4" strokeWidth={1.5} /> : <Square className="w-4 h-4" strokeWidth={1.5} />}
            </div>
          )}
          {/* Multi-photo indicator — shown when this scan has at least one
              extra reference photo. Signals to the user that they can swipe
              in the detail page to see additional angles. */}
          {(item.extra_images_count || 0) > 0 && !selectMode && (
            <div
              data-testid={`photo-count-badge-${item.scan_id}`}
              aria-label={t("gallery.card.photoCountAria", { n: 1 + item.extra_images_count })}
              className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/55 text-white text-[10px] px-2 py-0.5 backdrop-blur-sm"
            >
              <Layers className="w-3 h-3" strokeWidth={1.75} aria-hidden />
              <span className="font-medium [font-variant-numeric:lining-nums_tabular-nums]">{1 + item.extra_images_count}</span>
            </div>
          )}
        </div>
        <div className="p-3">
          <p className="font-display text-lg text-leaf-ink leading-tight truncate">
            {needsId ? t("gallery.card.unidentified") : (id.common_name || t("gallery.card.unknown"))}
          </p>
          {!needsId && id.scientific_name && <p className="italic text-[11px] text-leaf-mist truncate">{id.scientific_name}</p>}
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {needsId && <span className="badge-pill bg-[#FFF4DC] text-[#8a6a14]">{t("gallery.card.needsId")}</span>}
            {!needsId && idNative(id) === true && (
              <span className="badge-pill bg-[#E6EFE9] text-leaf">
                {t("gallery.card.stateNative", { code: stateCode(item?.state || userState) })}
              </span>
            )}
            {!needsId && (id.plant_type || "").toLowerCase() === "weed" && <span className="badge-pill bg-[#FDF0E7] text-rust">{t("gallery.card.weed")}</span>}
            {!needsId && id.is_keystone && <span className="badge-pill bg-[#E9F0D9] text-[#4A5D23]">{t("gallery.card.keystone")}</span>}
          </div>
        </div>
      </button>

      {/* User-authored tag chips.
          Rendered as a SIBLING of the outer <button> — not nested inside
          it — because HTML disallows button-in-button and screen readers
          get confused when they see one. Sibling placement keeps the
          layout identical (chips still sit at the bottom of the card)
          while letting each chip be a real button with its own tap
          target + a11y semantics.
          Cap at 2 visible chips so tall names + several attribute pills
          don't push the card off its aspect ratio; overflow shows as a
          `+N` counter that the operator can chase in the chip filter
          row above. When `onTagClick` is provided, each chip toggles
          that tag in the Gallery's URL-driven filter; chips already in
          the active filter are visually highlighted so a re-tap-to-
          remove is discoverable. */}
      {!needsId && Array.isArray(item.tags) && item.tags.length > 0 && (
        <div
          className="px-3 pb-3 -mt-1 flex gap-1 flex-wrap"
          data-testid={`plant-card-tags-${item.scan_id}`}
        >
          {item.tags.slice(0, 2).map((tag) => {
            const isActive = activeTags?.has?.(String(tag).toLowerCase());
            const clickable = typeof onTagClick === "function" && !selectMode;
            const commonProps = {
              "data-testid": `plant-card-tag-${item.scan_id}-${tag}`,
              title: tag,
              className: `inline-flex items-center rounded-full text-[10px] px-2 py-0.5 max-w-full transition ${
                isActive
                  ? "bg-leaf text-white"
                  : "bg-parchment text-leaf-mist"
              } ${clickable ? "hover:bg-leaf/10 hover:text-leaf-deep cursor-pointer" : ""}`,
              children: <span className="truncate max-w-[8rem]">#{tag}</span>,
            };
            return clickable ? (
              <button
                key={tag}
                type="button"
                onClick={(e) => {
                  // The card wrapper listens for taps to open the plant
                  // — stop propagation so a chip tap never doubles as a
                  // "navigate to detail" action.
                  e.stopPropagation();
                  onTagClick(tag);
                }}
                aria-pressed={!!isActive}
                {...commonProps}
              />
            ) : (
              <span key={tag} {...commonProps} />
            );
          })}
          {item.tags.length > 2 && (
            <span
              data-testid={`plant-card-tags-overflow-${item.scan_id}`}
              className="inline-flex items-center rounded-full bg-parchment text-leaf-mist text-[10px] px-2 py-0.5"
              title={item.tags.slice(2).join(", ")}
            >
              +{item.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {!selectMode && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRequestDelete(item.scan_id); }}
          data-testid={`delete-button-${item.scan_id}`}
          aria-hidden={!isHolding}
          tabIndex={isHolding ? 0 : -1}
          className={`hidden md:flex absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/85 backdrop-blur items-center justify-center text-rust/80 hover:text-rust transition shadow-botanical ${
            isHolding ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          aria-label={t("gallery.card.deleteAria")}>
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
};

export default PlantCard;
